import type { PreparedBarometerAuction } from '../types/components/AuctionsData'
import { useAutobidAuth } from '@autobid/nuxt-auth/src/composables/useAutobidAuth'
import { onServerPrefetch } from 'vue'
import { useCarPageInfo } from './car/useCarPageInfo'
import { useCheckAccessToAuction } from './useAccessToAuction'
import { useAuction } from './useAuction'

const ALLOWED_AUCTION_APP_IDS: Record<number, number[]> = {
  1: [1, 20, 33], // live/parts auctions should be allowed to display on the premium application
}

function isAuctionAppIdAllowed(currentAppId: number, auctionAppId: number) {
  const allowedList = ALLOWED_AUCTION_APP_IDS[currentAppId]

  if (!allowedList) {
    return auctionAppId === currentAppId
  }

  return allowedList.includes(auctionAppId)
}

/**
 * Function that verifies multiple condition to check if user has access to the auction, or if the slug of auction is correct.
 */
export async function useAuctionValidator(id: number | undefined) {
  const { pageType } = useCarPageInfo()
  const { APP_ID } = useRuntimeConfig().public
  const route = useRoute()
  const { t } = useI18n()
  const { auction, suspense } = useAuction(id)
  const { isAuthed, setPageRedirectData } = useAutobidAuth()
  const checkAccessToAuction = useCheckAccessToAuction()
  const updatePageRedirectData = (data: PreparedBarometerAuction) => {
    if (!isAuthed.value) {
      setPageRedirectData({
        redirectable: true,
      })
      return
    }

    const willBeAccessAfterAuthChange = checkAccessToAuction(data.restrictions, data.appId, isAuthed.value)
    setPageRedirectData({
      redirectable: willBeAccessAfterAuthChange.statusCode === 200,
    })
  }

  onServerPrefetch(suspense)
  await suspense()

  const validate = () => {
    if (!auction.value) {
      showError({ statusCode: 404 })
      return
    }

    const shouldSlugBeValidated
      = pageType.value === 'barometerCurrent' || pageType.value === 'carList'
    const access = checkAccessToAuction(auction.value.restrictions, auction.value.appId, isAuthed.value)

    let status = 200

    if (access.statusCode !== 200) {
      updatePageRedirectData(auction.value)
      return showError(access)
    }
    else if (
      shouldSlugBeValidated
      && auction.value.slug?.trim() !== route.params.slug
    ) {
      status = 404
    }
    else if (!isAuctionAppIdAllowed(APP_ID, auction.value.appId)) {
      status = 403
    }

    updatePageRedirectData(auction.value)

    if (status !== 200) {
      showError({
        statusCode: status,
        statusMessage: t(`error-page.${status}.description`),
      })
    }
  }

  validate()
}
